/* Tour catalogue — workflow-categorised, library-ready definitions.
 *
 * Two layers of consumers:
 *   - TourProvider reads `TOURS[id].steps` to run a single walkthrough.
 *   - TourLibrary reads `TOUR_CATALOGUE` to render the cards grid (icon,
 *     title, description, duration, category, completion state).
 *
 * Each tour has:
 *   - role: which user role this tour is meant for
 *   - title: card title in the library
 *   - description: card subtitle (one line, ~80 chars)
 *   - icon: phosphor icon component name (resolved in the library page)
 *   - duration: display string ("~5 min")
 *   - category: one of CASES | PLAN | PATIENTS | SCHEDULE | COMMS | BILLING | IDENTITY
 *   - order: card ordering inside the category
 *   - available: true if authored, false for "coming soon" stubs
 *   - steps?: tour step definitions (only when available=true)
 *
 * Step shape:
 *   - title:     short headline
 *   - body:      1-3 sentences explaining the feature
 *   - route?:    push-navigate to this path before painting
 *   - target?:   data-testid selector (omit for centered modal step)
 *   - placement: 'top' | 'bottom' | 'left' | 'right' | 'auto'
 */

export const TOUR_CATEGORIES = {
    CASES:    { label: "Case lifecycle", color: "violet" },
    PLAN:     { label: "Plan review",    color: "teal" },
    PATIENTS: { label: "Patients",       color: "blue" },
    SCHEDULE: { label: "Schedule",       color: "amber" },
    COMMS:    { label: "Communications", color: "cyan" },
    BILLING:  { label: "Billing",        color: "emerald" },
    IDENTITY: { label: "Clinic identity", color: "rose" },
};

/* The 8 doctor-side tours. Top 3 are authored; the other 5 are
 * "coming soon" stubs that show in the library so the doctor sees the
 * full roadmap. */
export const DOCTOR_TOUR_IDS = [
    "doctor_submit_case",
    "doctor_approve_plan",
    "doctor_request_revision",
    "doctor_manage_patients",
    "doctor_daily_schedule",
    "doctor_communications",
    "doctor_billing",
    "doctor_clinic_identity",
];

export const TOURS = {
    /* Legacy "everything in one" portal overview — kept for back-compat
       with the `?tour=doctor` URL hook used in the original iter60 flow. */
    doctor: {
        role: "doctor",
        title: "Portal overview",
        description: "Quick 90-second sweep of where everything lives in your portal.",
        icon: "Compass",
        duration: "~90 sec",
        category: "CASES",
        order: 0,
        available: true,
        steps: [
            {
                title: "Welcome to your Invisique™ provider portal",
                body: "Let's take a 90-second tour of the features built for you and your clinic team. You can re-run it anytime from your profile menu — and skip it any time by pressing Esc.",
                route: "/doctor",
            },
            {
                title: "Aligner Cases — your case lifecycle hub",
                body: "Every patient case, from submission to retention, lives here. Click into a case to see structured planning, treatment notes, and a full audit timeline.",
                route: "/doctor/cases",
                target: "aligner-tabbar",
                placement: "bottom",
            },
            {
                title: "Compliance Insights — wear-time & risk at a glance",
                body: "The Compliance Insights tab surfaces patients who are slipping (low wear, missed check-ins) so you intervene before refinements become inevitable.",
                route: "/doctor/cases",
                target: "tab-insights",
                placement: "bottom",
            },
            {
                title: "Today's Appointments + Patient reschedule inbox",
                body: "When patients want to reschedule, they propose up to 3 slots. You review the inbox here and approve one with a click. Saves 60-70% of reschedule calls.",
                route: "/doctor/appointments",
                target: "reschedule-inbox",
                placement: "bottom",
            },
            {
                title: "My Patients — every patient, all your data",
                body: "Your full caseload at a glance with treatment status and quick filters. Open any patient to see their cases, appointments, treatment notes, and complete medical profile.",
                route: "/doctor/patients",
                placement: "auto",
            },
            {
                title: "Billing & Payouts",
                body: "Two tabs: patient invoices (what your clinic charges) and Invisique™ invoices (what HQ charges you). The red \"X due\" chip never lets you forget an unpaid HQ bill.",
                route: "/doctor/billing",
                target: "billing-toptabs",
                placement: "bottom",
            },
            {
                title: "That's the highlight reel!",
                body: "There's plenty more — clinic staff management, locations, business hours, and 24/7 in-app chat with our COE. Take the focused tours from the Help & Tours page when you're ready.",
                route: "/doctor",
            },
        ],
    },

    /* ── TOUR 1 — Submit your first case ───────────────────────── */
    doctor_submit_case: {
        role: "doctor",
        title: "Submit your first case",
        description: "End-to-end walkthrough of the case wizard, from records to submission.",
        icon: "Cube",
        duration: "~5 min",
        category: "CASES",
        order: 1,
        available: true,
        steps: [
            {
                title: "Submit your first case",
                body: "This 6-step tour shows you exactly how to send a new aligner case to our planning team. You can pause anytime with Esc.",
                route: "/doctor",
            },
            {
                title: "Aligner Cases — your home for every case",
                body: "All your cases live here, sorted by lifecycle stage. The pipeline view shows which cases need your action versus which are with the planner.",
                route: "/doctor/cases",
                target: "portal-nav-aligner-cases",
                placement: "right",
            },
            {
                title: "Click \"+ New Case\" to start",
                body: "The button at the top-right of this page opens the case submission wizard. You can also start from a patient profile — both routes land at the same wizard.",
                route: "/doctor/cases",
                target: "new-case-btn",
                placement: "left",
            },
            {
                title: "Step 1 — Choose patient + planning mode",
                body: "Pick an existing patient or add a new one, then decide: do you want our Centre of Excellence team to plan this case, or are you authoring the plan yourself? You can switch later if needed.",
                route: "/doctor/cases/new",
                target: "case-wizard-patient-field",
                placement: "bottom",
            },
            {
                title: "Steps 2-5 — Records, clinical exam, prescription",
                body: "Upload STL scans + intraoral photos, capture diagnosis (overjet, overbite, crowding), and add your prescription (IPR preference, attachments, elastics). Everything autosaves as you type.",
                route: "/doctor/cases/new",
                target: "case-wizard-next",
                placement: "top",
            },
            {
                title: "Step 6 — Pick a complexity tier",
                body: "Tiers are clinically calibrated — Tier 1 is straightforward, Tier 5 is complex. The price card on the right updates live as you select.",
                route: "/doctor/cases/new",
                placement: "auto",
            },
            {
                title: "Step 7 — Review and submit",
                body: "Final pre-flight: 5 readiness checks make sure nothing critical is missing. Hit Submit and your case lands with a planner within minutes. You'll get a notification when the plan is ready (typically 24-48h).",
                route: "/doctor/cases/new",
                target: "case-wizard-submit",
                placement: "top",
            },
            {
                title: "You're done!",
                body: "When the planner finishes, you'll see a Pending Approval pill on the case. Take the \"Read & approve a plan\" tour next to learn that flow.",
                route: "/doctor/cases",
            },
        ],
    },

    /* ── TOUR 2 — Read & approve a plan ────────────────────────── */
    doctor_approve_plan: {
        role: "doctor",
        title: "Read & approve a plan",
        description: "Reviewing the planner's setup — the diff view, plan viewer, and approval flow.",
        icon: "CheckCircle",
        duration: "~3 min",
        category: "PLAN",
        order: 1,
        available: true,
        steps: [
            {
                title: "When a plan is ready, you'll see a pill",
                body: "Cases waiting for your approval show a teal \"Pending Approval\" pill on the case card. Click into the case to start the review.",
                route: "/doctor/cases",
            },
            {
                title: "Open the Plan Viewer link",
                body: "Click the plan viewer link inside the case to open the planner's 3D setup — this is the visual representation of every stage. Take 60 seconds to scrub through it before deciding.",
                route: "/doctor/cases",
                placement: "auto",
            },
            {
                title: "Open the approval review page",
                body: "From the case detail, the \"Review plan →\" button takes you to a side-by-side view. The original prescription is on the left; the planner's interpretation is on the right.",
                route: "/doctor/cases",
                placement: "auto",
            },
            {
                title: "Approve — case moves to production",
                body: "Hit Approve when you're satisfied. The case immediately ships to manufacturing. We'll send you a notification when the aligners arrive at your clinic.",
                route: "/doctor/cases",
                placement: "auto",
            },
            {
                title: "Need changes? Request a revision",
                body: "If something needs tweaking, click \"Request revision\" instead. Take the \"Request a revision\" tour next for the new inline workflow that makes back-and-forth painless.",
                route: "/doctor/cases",
            },
        ],
    },

    /* ── TOUR 3 — Request a revision (NEW inline UX) ───────────── */
    doctor_request_revision: {
        role: "doctor",
        title: "Request a revision",
        description: "How to ask the planner for changes — and what happens after they respond.",
        icon: "ArrowsClockwise",
        duration: "~3 min",
        category: "PLAN",
        order: 2,
        available: true,
        steps: [
            {
                title: "Request a revision — quick & focused",
                body: "When the planner's setup needs tweaks, the revision form lets you ask for exactly what you want changed — no need to describe the whole plan again.",
                route: "/doctor/cases",
            },
            {
                title: "Side-by-side: their plan vs your asks",
                body: "The form shows the planner's values on the left (read-only). You fill in only the fields you want changed on the right — leave the rest blank to keep their value. Add a free-text comment if the change needs explanation.",
                route: "/doctor/cases",
                placement: "auto",
            },
            {
                title: "Submit — your form locks until the planner responds",
                body: "Once you click Send, the page locks (we don't want you tweaking the request while the planner is working). You'll get a notification the moment the revised plan is back.",
                route: "/doctor/cases",
                placement: "auto",
            },
            {
                title: "Planner's response — Accepted / Denied per row",
                body: "When the planner resubmits, you'll see a card at the top of your review page with each ask labelled: green ✓ \"Accepted · applied\" if they made the change, or amber \"Denied\" with their rationale if they didn't. No more guessing what was actually done.",
                route: "/doctor/cases",
                placement: "auto",
            },
            {
                title: "Approve or push back",
                body: "If you're satisfied, hit Approve. If something still isn't right, you can request another revision — each round is tracked in the case history with the planner's rationale preserved.",
                route: "/doctor/cases",
            },
        ],
    },

    /* ── TOUR 4 — Manage your patients ─────────────────────────── */
    doctor_manage_patients: {
        role: "doctor",
        title: "Manage your patients",
        description: "Adding walk-ins, booking scan visits, patient profile deep-dive.",
        icon: "Users",
        duration: "~4 min",
        category: "PATIENTS",
        order: 1,
        available: true,
        steps: [
            {
                title: "Your patient roster lives here",
                body: "Every patient under your care — aligner cases or not — lives in My Patients. Sortable, filterable, exportable to CSV.",
                route: "/doctor/patients",
                target: "doctor-patients-page",
                placement: "auto",
            },
            {
                title: "Add a new patient",
                body: "Click \"+ Add patient\" to register a walk-in. Just name + phone is enough — the patient can fill in the rest themselves via OTP login.",
                route: "/doctor/patients",
                target: "doctor-add-patient-btn",
                placement: "left",
            },
            {
                title: "Filter by treatment phase",
                body: "Use the phase filter to slice your roster: who's in scan, in treatment, in retention, or just enquired. Combine with the search box for instant deep-dives.",
                route: "/doctor/patients",
                target: "patients-phase-filter",
                placement: "bottom",
            },
            {
                title: "Quick actions from the side drawer",
                body: "Click any patient row to open a side drawer with a 30-second summary: current aligner, wear-time, treatment timeline, and quick-action buttons (Book appointment, Record payment, Request payment, Add note, Patient chat).",
                route: "/doctor/patients",
                placement: "auto",
            },
            {
                title: "Open the full profile for everything",
                body: "Click \"Full profile →\" in the drawer (or the patient's name) to see their full medical history, every case, every appointment, every invoice, every chat. It's the patient's single source of truth.",
                route: "/doctor/patients",
                placement: "auto",
            },
            {
                title: "Bulk actions when you need them",
                body: "Select multiple patients with checkboxes to bulk-tag them (e.g. \"VIP\", \"insurance-covered\") or message them in one shot. Useful for campaigns or quarterly check-ins.",
                route: "/doctor/patients",
                target: "bulk-action-bar",
                placement: "top",
            },
            {
                title: "Export anytime",
                body: "The \"Export CSV\" button on the top-right gives you a full dump for accounting, marketing, or insurance claims. Filtering applies — so you can export just one segment.",
                route: "/doctor/patients",
                target: "doctor-export-patients-btn",
                placement: "left",
            },
            {
                title: "You're set!",
                body: "Patients flow from here into appointments, cases, billing, and chat — they're the centre of gravity of your portal. Take the Daily Schedule tour next to learn the appointments side.",
                route: "/doctor/patients",
            },
        ],
    },

    /* ── TOUR 5 — Run your daily schedule ──────────────────────── */
    doctor_daily_schedule: {
        role: "doctor",
        title: "Run your daily schedule",
        description: "Booking appointments, the reschedule inbox, and no-show handling.",
        icon: "Calendar",
        duration: "~3 min",
        category: "SCHEDULE",
        order: 1,
        available: true,
        steps: [
            {
                title: "Your schedule at a glance",
                body: "Today's view shows every booked slot, walk-in window, and gap. Mondays through Sundays are tabbed at the top. Toggle the month view from the calendar icon.",
                route: "/doctor/appointments",
                target: "doctor-appointments-page",
                placement: "auto",
            },
            {
                title: "Quick-book a walk-in",
                body: "Patient just showed up unannounced? Click \"+ Walk-in\" to slot them into the next available gap without leaving the schedule.",
                route: "/doctor/appointments",
                target: "walk-in-btn",
                placement: "left",
            },
            {
                title: "Patient reschedule inbox",
                body: "When patients want to reschedule, they don't call — they propose up to 3 slots that work for them. You see them all here. One click approves; one click declines (with a note). Saves 60-70% of phone tag.",
                route: "/doctor/appointments",
                target: "reschedule-inbox",
                placement: "top",
            },
            {
                title: "Click any slot to see who & what",
                body: "Every booked slot is a card with the patient's name, treatment type, and notes. Right-click for quick actions: mark no-show, mark complete, reschedule, cancel.",
                route: "/doctor/appointments",
                placement: "auto",
            },
            {
                title: "Done — schedule mastered",
                body: "Appointments tie into compliance: missed visits and no-shows show up on your dashboard's Compliance section so you can intervene before treatment slips.",
                route: "/doctor/appointments",
            },
        ],
    },

    /* ── TOUR 6 — Lab Chat vs Patient Chat ─────────────────────── */
    doctor_communications: {
        role: "doctor",
        title: "Lab Chat vs Patient Chat",
        description: "When to use each, and how chat unlocks once a case lands.",
        icon: "ChatsCircle",
        duration: "~2 min",
        category: "COMMS",
        order: 1,
        available: true,
        steps: [
            {
                title: "Two distinct chats — two distinct audiences",
                body: "Invisique™ gives you two separate chat channels: one for our planning team (Lab Chat) and one for your patients (Patient Chat). They never mix, so you can speak frankly in each.",
                route: "/doctor/lab-chat",
            },
            {
                title: "Lab Chat — talk to your planner",
                body: "This is your back-and-forth with our Centre of Excellence team. Ask clinical questions, push back on a plan, request a refinement — every conversation is threaded per case so nothing gets lost.",
                route: "/doctor/lab-chat",
                target: "portal-nav-lab-chat",
                placement: "right",
            },
            {
                title: "Patient Chat — talk to your patients",
                body: "Direct line to every patient under your care. Send photos, ask compliance questions, send reminders. The patient sees these in their own portal under \"Chat with your doctor\".",
                route: "/doctor/patient-chat",
                target: "portal-nav-patient-chat",
                placement: "right",
            },
            {
                title: "Searchable + audit-logged",
                body: "Both chats are searchable across all your conversations and audit-logged (so disputes get resolved fast). Patient chats unlock automatically once their treatment is activated.",
                route: "/doctor/patient-chat",
            },
            {
                title: "Pro tip",
                body: "Set a habit: scan Lab Chat once a day, Patient Chat twice a day. The unread badges in your sidebar will tell you when you're behind.",
                route: "/doctor",
            },
        ],
    },

    /* ── TOUR 7 — Billing & offers ─────────────────────────────── */
    doctor_billing: {
        role: "doctor",
        title: "Billing & offers",
        description: "Patient invoices, Invisique™ invoices, advance payments, applying offers.",
        icon: "Receipt",
        duration: "~3 min",
        category: "BILLING",
        order: 1,
        available: true,
        steps: [
            {
                title: "Billing has two sides",
                body: "Patient invoices are what your clinic charges your patients. Invisique™ invoices are what HQ charges you for manufacturing. The tabs at the top let you switch.",
                route: "/doctor/billing",
                target: "billing-toptabs",
                placement: "bottom",
            },
            {
                title: "Patient invoices side — your revenue",
                body: "Create, send, and track invoices to your patients. Status chips tell you at a glance who's paid, who's overdue, and who's been reminded. Bulk-remind everyone overdue with one button.",
                route: "/doctor/billing",
                target: "billing-top-patient",
                placement: "bottom",
            },
            {
                title: "Quick stats up top",
                body: "The summary cards give you outstanding A/R, this month's collections, and the count of overdue accounts in one glance. Click any card to filter the table.",
                route: "/doctor/billing",
                target: "billing-summary",
                placement: "bottom",
            },
            {
                title: "Invisique™ invoices side — your costs",
                body: "Switch to this tab to see every invoice from HQ for cases you've shipped. The red \"X due\" chip never lets you forget an unpaid manufacturing bill — pay them on time to keep your priority queue.",
                route: "/doctor/billing",
                target: "billing-top-invisique",
                placement: "bottom",
            },
            {
                title: "Offers travel into your invoices",
                body: "Active offers (visible on your Offers page) auto-apply to qualifying cases — soft-launch discounts, volume tiers, referral credits. The discount line is visible on every patient invoice so nothing's hidden.",
                route: "/doctor/offers",
                target: "portal-nav-offers",
                placement: "right",
            },
            {
                title: "All done — money in, money out, transparent",
                body: "Two tabs, three quick stats, full transparency on every rupee. Export anytime for your accountant.",
                route: "/doctor/billing",
            },
        ],
    },

    /* ── TOUR 8 — Your clinic identity ─────────────────────────── */
    doctor_clinic_identity: {
        role: "doctor",
        title: "Your clinic identity",
        description: "Logo, signature, and how your branding shows up on patient cards.",
        icon: "UserCircle",
        duration: "~2 min",
        category: "IDENTITY",
        order: 1,
        available: true,
        steps: [
            {
                title: "Your profile is your patient-facing brand",
                body: "Patients see your name, photo, qualifications, and clinic info every time they open their portal. Spend 5 minutes here once and it'll pay off forever.",
                route: "/doctor/profile",
                target: "profile-hero",
                placement: "auto",
            },
            {
                title: "Upload a clean profile photo",
                body: "Drop a square headshot here. Patients trust faces — and your photo appears next to every message you send through Patient Chat. Use a clinical setting, not a wedding selfie.",
                route: "/doctor/profile",
                target: "profile-avatar-dropzone",
                placement: "right",
            },
            {
                title: "Your bio sets expectations",
                body: "A short paragraph (~2-3 sentences) about your practice, philosophy, and what patients can expect. Keep it warm and direct. Skip jargon.",
                route: "/doctor/profile",
                target: "profile-hero-bio",
                placement: "top",
            },
            {
                title: "Tabs for the rest",
                body: "Switch tabs to manage qualifications + experience, working hours, clinic locations, GSTIN + billing footer (for invoices), and your bank details (for HQ payouts). The completeness ring on the top right tells you how much is filled.",
                route: "/doctor/profile",
                target: "profile-tab-strip",
                placement: "bottom",
            },
            {
                title: "Branding flows everywhere",
                body: "Your logo + clinic name show up on every invoice, every patient card, every clinic chat header. Get it right once.",
                route: "/doctor/profile",
            },
        ],
    },

    /* ── Patient portal tour (existing) — preserved ────────────── */
    patient: {
        role: "patient",
        title: "Patient portal overview",
        description: "Where to find your treatment progress, appointments, and chat.",
        icon: "Compass",
        duration: "~90 sec",
        category: "CASES",
        order: 0,
        available: true,
        steps: [
            {
                title: "Welcome to your Invisique™ patient portal",
                body: "A quick tour of where to find what — your treatment progress, appointments, your care team, and how to chat with your doctor.",
                route: "/patient",
            },
            {
                title: "Your aligner progress",
                body: "Your current aligner, days logged, wear-time average, streak, and next-switch ETA — everything you'd ask your doctor between visits, in one card.",
                route: "/patient",
                target: "my-aligner-progress",
                placement: "auto",
            },
            {
                title: "Your appointments",
                body: "Upcoming visits, past visits, and cancelled ones — all in one place. Need to reschedule? Propose 3 slots you're free; your clinic confirms one of them.",
                route: "/patient/appointments",
                placement: "auto",
            },
            {
                title: "Chat with your doctor",
                body: "Direct line to your clinic. Send questions, photos of issues, or just say hi. Your doctor responds in their own portal — no third-party calls or emails.",
                route: "/patient/clinic-chat",
                placement: "auto",
            },
            {
                title: "All set!",
                body: "That's the patient portal. Take your time exploring — you can re-run this tour anytime from your profile menu.",
                route: "/patient",
            },
        ],
    },
};
